import React, { useState } from "react";
import { FormHelperText, Grid, Stack } from "@mui/material";
import Icon from "src/components/icon";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import ExternalLink from "src/components/external-link";
import Button from "@mui/material/Button";
import handleCopy from "src/scenes/developer-portal/handle-copy";
import { ToastContext } from "src/components/providers/toast-provider";
import request from "../../../utils/request";
import { useAuth } from "../../../components/providers/auth-context";
import googleWorkspace from "../../../assets/icons/google-workspace";

const H1 = styled.h1`
    font-size: 22px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const P = styled.h2`
    font-size: 12px;
    font-weight: normal;
    margin-top: 12px;
    margin-bottom: 12px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const PermissionsIcon = styled(Icon)`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px 12px 24px 12px;
    margin: 32px 0 32px 0;
`;

const CopyButton = styled(Button)`
    max-width: 430px;
    text-transform: none;
    font-size: 12px;
    text-align: left;
    margin: 12px;
    color: ${({ theme }) => theme.palette.secondary.main};
    background: #3f3f3f;
`;

const PermissionsGoogleOrg = () => {
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const navigate = useNavigate();
    const { user } = useAuth();
    const [email, setEmail] = useState(user.emails[0]);
    const [error, setError] = useState();
    const [loading, setLoading] = useState();
    const type = "admin";

    const handleClick = async () => {
        if (!type) {
            setError("Select how you want to integrate");
        } else if (type === "admin" && !email) {
            setError("Email is required for admin setup");
        } else {
            try {
                setLoading(true);
                await request(`api/google/centrally-managed`, {
                    method: "POST",
                    body: JSON.stringify({ email }),
                });
                setLoading(false);
                navigate("/integrations/success/google");
            } catch (e) {
                setLoading(false);
                if (e.status === 400) {
                    setError(`${e.message} Please follow the instructions above.`);
                }
            }
        }
    };

    const AuthText =
        "https://www.googleapis.com/auth/calendar.readonly, https://www.googleapis.com/auth/admin.directory.user.readonly, https://www.googleapis.com/auth/admin.directory.group.readonly";
    const ClientIdText = "111052023020764232569";

    return (
        <>
            <Helmet>
                <title>Adadot - Google Permissions</title>
            </Helmet>
            <Grid container justifyContent="center" mb={8}>
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    style={{ height: "100%", width: "70%" }}
                >
                    <H1>Google Company Wide Integration</H1>
                    <H2>How to setup your company wide Google integration</H2>
                    <Stack direction="row">
                        <PermissionsIcon svg={googleWorkspace} width="100px" height="80px" />
                    </Stack>
                    {type === "admin" && (
                        <>
                            <P>
                                Control API access with <strong>domain-wide delegation</strong> as
                                shown below
                            </P>
                            <P>&#8226; Go to your google admin console (at admin.google.com)</P>
                            <P>
                                &#8226; In the Admin console, go to <strong>Menu</strong> &gt;{" "}
                                <strong>Security</strong> &gt;{" "}
                                <strong>Access and data control</strong> &gt;{" "}
                                <strong>API control</strong> &gt;{" "}
                                <ExternalLink
                                    href="https://admin.google.com/ac/owl/domainwidedelegation"
                                    underline="hover"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Manage Domain Wide Delegation.
                                </ExternalLink>
                            </P>
                            <img
                                src="https://assets-sit.adadot.com/integrations-images/stepOneGoogle.png"
                                alt=""
                                style={{ maxWidth: "100vw", maxHeight: "44vh" }}
                            />
                            <P>
                                &#8226; Click <strong>Add new</strong> and enter the{" "}
                                <strong>Client ID</strong> and <strong>OAuth Scopes</strong>
                                bellow
                            </P>
                            <Stack direction="row" spacing={1} mb={2} mt={1}>
                                <P style={{ width: "142px" }}>&#8226; Client ID :</P>
                                <CopyButton
                                    onClick={() =>
                                        handleCopy({
                                            value: ClientIdText,
                                            setMessage,
                                            setSeverity,
                                            setIsToastTriggered,
                                            type: "cid",
                                        })
                                    }
                                >
                                    {ClientIdText}
                                </CopyButton>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <P style={{ width: "142px" }}>&#8226; OAuth Scopes:</P>{" "}
                                <CopyButton
                                    onClick={() =>
                                        handleCopy({
                                            value: AuthText,
                                            setMessage,
                                            setSeverity,
                                            setIsToastTriggered,
                                            type: "scopes",
                                        })
                                    }
                                >
                                    {AuthText}
                                </CopyButton>
                            </Stack>

                            <img
                                src="https://assets-sit.adadot.com/integrations-images/stepTwoGoogle.png"
                                alt=""
                                style={{ maxWidth: "100vw", maxHeight: "44vh" }}
                            />
                            <P>
                                &#8226; Click <strong>Authorize</strong>. If you get an error, the{" "}
                                <strong>Client ID</strong> might not be registered with Google or
                                there might be duplicate or unsupported scopes.
                            </P>
                            <img
                                src="https://assets-sit.adadot.com/integrations-images/stepThreeGoogle.png"
                                alt=""
                                style={{ maxWidth: "100vw", maxHeight: "44vh" }}
                            />
                            <P>
                                &#8226; Changes can take up to 24 hours but typically happen more
                                quickly
                            </P>
                            <P>
                                &#8226; <strong>Super-Admin Email:</strong> Enter the company&apos;s
                                google workspace email that has the super admin permission.
                            </P>
                            <Stack
                                direction="row"
                                mt={2}
                                spacing={2}
                                alignItems="center"
                                style={{ width: "100%" }}
                            >
                                <TextField
                                    required
                                    fullWidth
                                    style={{ maxWidth: "500px" }}
                                    id="outlined-required"
                                    label="Google Admin Email"
                                    value={email}
                                    onChange={(event) => {
                                        setError(undefined);
                                        setEmail(event.target.value);
                                    }}
                                />
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    onClick={handleClick}
                                    style={{ width: "102px" }}
                                    loading={loading}
                                >
                                    Connect
                                </LoadingButton>
                            </Stack>
                        </>
                    )}
                    {error && (
                        <FormHelperText error sx={{ fontSize: "15px", fontWeight: "bold" }}>
                            {error}
                        </FormHelperText>
                    )}
                </Stack>
            </Grid>
        </>
    );
};

export default PermissionsGoogleOrg;
